// 스테이지 지도 환경(S2 지역1·S6 지역2): 벽으로 둘러싼 사각 방 대신 낭떠러지·물·뿌리·현무암 기둥 가장자리를 가진 판.
// 지역2(잿불 협곡)는 빈칸 아래가 용암 바다(MapDef.seaY)이고, 하늘은 잿불 하늘, 바닥 균열·절벽 아래가 용암빛으로 달아오른다.
// 구성: 판정 격자 → 거리장 등고선(stageEdge) → 바닥 윗면(깊은 안쪽 사각형 + 가장자리 띠) · 절벽/둑 단면 · 수면 ·
//       가장자리 트림(세트피스 인스턴싱, 표본마다 한 조각) · 지도 앵커 세트피스 · 랜드마크 키트 · 풀/덩굴/빛줄기/불꽃.
// 모든 로드는 start() 하나로 끝나고(실패한 조각은 빠진다), 프레임마다 하는 일은 cull()뿐이다(EnvVisual과 같은 규약).
import * as THREE from 'three/webgpu';
import type { QualityLevel } from '../../shared/config';
import type { MapDef, MapGrid, Rect, SetPieceDef } from '../../shared/levels';
import { cellsOf, rectsOfCells } from '../../shared/shapes';
import { compose } from '../util';
import { cardField, contactField, flameField, glowField, ivyField, shaftField } from './fx';
import type { EnvResult } from './index';
import { loadKit, loadTexture, track, type KitAsset } from './kits';
import type { Emitter } from './layout';
import { moodOf } from './mood';
import { mulberry32, valueNoise } from './rng';
import { loadSet, loadSetAtlas, setMaterial, type SetAsset, type SetAtlas } from './sets';
import { SCATTER_SETS, STYLE_MODULES, arcadeEveryOf, isEmberLook, landmarkSet, lookOf, setRegion, usedSets, type SetName, type StageLookKey } from './stageSets';
import { buildField, contourOf, edgeProximity, edgeStyleAt, inWater, resample, shoreDistance, type Chain } from './stageEdge';
import {
  dataTexture, inlayMedallionMesh, lavaSeaMaterial, lavafallMaterial, runeRingMesh, stageBasaltCliffMaterial, stageCliffMaterial, stageEmberFloorMaterial, stageFloorMaterial, stageWaterMaterial,
  type ProximityTex,
} from './stageSurfaces';
import { emberAtmosphere, emberHazards, emberSky } from './emberTerrain';
import { abs, float, mix, mx_noise_float, smoothstep, time, uv, vec3 } from 'three/tsl';
import { backdropMaterial, kitMaterial } from './surfaces';
import { DECOR_FLAG } from './decor';

/** 시야 사각형 바깥 여유(m) */
const CULL_MARGIN = 14;
/** 시야 중심(화면 가운데 시선이 바닥에 닿는 점)에서 이 거리(m) 밖의 인스턴스는 그리지 않는다 — 먼 곳은 안개에 묻혀 삼각형만 먹는다 */
const CULL_RADIUS = 38;
const CLIFF_DEPTH = 26;
const WATER_Y = -1.0;
const _ndc = new THREE.Vector3();
const _dir = new THREE.Vector3();
const DENSITY: Record<QualityLevel, number> = { low: 0.5, mid: 0.75, high: 1 };

type CutMode = 0 | 1 | 2;
interface Inst { x: number; y: number; z: number; rot: number; sx: number; sy: number; sz: number; tint: number; cut: CutMode; radius: number }
interface Batch { cullR: number; mesh: THREE.InstancedMesh; cut: THREE.InstancedBufferAttribute; n: number; mat: Float32Array; cutSrc: Float32Array; col: Float32Array; pos: Float32Array }

/** 세트피스 재질 옵션: 이름 → 컷 방식 */
interface SetStyle { clip: boolean; cap: readonly [number, number, number]; cut: CutMode; shadow: boolean }
const STONE_CAP: readonly [number, number, number] = [0.42, 0.44, 0.32], WOOD_CAP: readonly [number, number, number] = [0.34, 0.25, 0.15], BARK_CAP: readonly [number, number, number] = [0.3, 0.22, 0.13];
const BASALT_CAP: readonly [number, number, number] = [0.13, 0.13, 0.15], IRON_CAP: readonly [number, number, number] = [0.1, 0.09, 0.09];
const SET_STYLE: Readonly<Record<SetName, SetStyle>> = {
  edge_stone: { clip: true, cap: STONE_CAP, cut: 1, shadow: false }, edge_stone_b: { clip: true, cap: STONE_CAP, cut: 1, shadow: false },
  edge_parapet: { clip: true, cap: STONE_CAP, cut: 1, shadow: false }, edge_root: { clip: true, cap: BARK_CAP, cut: 1, shadow: false },
  edge_palisade: { clip: true, cap: WOOD_CAP, cut: 1, shadow: false }, edge_quay: { clip: true, cap: STONE_CAP, cut: 1, shadow: false },
  ruin_arcade: { clip: true, cap: STONE_CAP, cut: 1, shadow: false },
  bridge: { clip: true, cap: STONE_CAP, cut: 1, shadow: true }, bridge_wood: { clip: true, cap: WOOD_CAP, cut: 1, shadow: true }, bridge_root: { clip: true, cap: BARK_CAP, cut: 1, shadow: true },
  ruin_arch: { clip: false, cap: STONE_CAP, cut: 2, shadow: true }, root_gate: { clip: false, cap: BARK_CAP, cut: 2, shadow: true }, palisade_gate: { clip: false, cap: WOOD_CAP, cut: 2, shadow: true },
  tower: { clip: false, cap: WOOD_CAP, cut: 2, shadow: true }, throne: { clip: false, cap: WOOD_CAP, cut: 2, shadow: true },
  bonfire: { clip: true, cap: WOOD_CAP, cut: 1, shadow: false }, banner_pole: { clip: true, cap: WOOD_CAP, cut: 1, shadow: false }, bone_pile: { clip: true, cap: [0.6, 0.56, 0.46], cut: 1, shadow: false },
  far_cliff: { clip: false, cap: STONE_CAP, cut: 0, shadow: false }, far_aqueduct: { clip: false, cap: STONE_CAP, cut: 0, shadow: false },
  root_arc: { clip: true, cap: BARK_CAP, cut: 1, shadow: false }, root_knot: { clip: true, cap: BARK_CAP, cut: 2, shadow: true },
  prop_rock: { clip: true, cap: STONE_CAP, cut: 1, shadow: false }, prop_rubble: { clip: true, cap: STONE_CAP, cut: 1, shadow: false },
  prop_crates: { clip: true, cap: WOOD_CAP, cut: 1, shadow: false }, prop_mushrooms: { clip: true, cap: BARK_CAP, cut: 1, shadow: false },
  // 지역2(잿불 협곡): 현무암 단면은 검푸른색, 쇠 조각은 녹슨 검정
  e_edge_col: { clip: true, cap: BASALT_CAP, cut: 1, shadow: false }, e_edge_col_b: { clip: true, cap: BASALT_CAP, cut: 1, shadow: false },
  e_edge_chain: { clip: true, cap: BASALT_CAP, cut: 1, shadow: false }, e_edge_forge: { clip: true, cap: IRON_CAP, cut: 1, shadow: false },
  e_bridge: { clip: true, cap: BASALT_CAP, cut: 1, shadow: true }, e_gate: { clip: false, cap: BASALT_CAP, cut: 2, shadow: true },
  e_brazier: { clip: false, cap: BASALT_CAP, cut: 2, shadow: true }, e_fire_bowl: { clip: true, cap: IRON_CAP, cut: 1, shadow: false },
  e_banner: { clip: false, cap: IRON_CAP, cut: 2, shadow: true }, e_chain_span: { clip: false, cap: IRON_CAP, cut: 0, shadow: false },
  e_crucible: { clip: false, cap: IRON_CAP, cut: 2, shadow: true }, e_anvil: { clip: true, cap: IRON_CAP, cut: 1, shadow: true },
  e_altar: { clip: false, cap: BASALT_CAP, cut: 2, shadow: true }, e_obelisk: { clip: false, cap: BASALT_CAP, cut: 2, shadow: true },
  e_basalt: { clip: false, cap: BASALT_CAP, cut: 2, shadow: true }, e_rubble: { clip: true, cap: BASALT_CAP, cut: 1, shadow: false },
  e_prop_rock: { clip: true, cap: BASALT_CAP, cut: 1, shadow: false }, e_prop_slag: { clip: true, cap: BASALT_CAP, cut: 1, shadow: false },
  e_prop_crystal: { clip: true, cap: BASALT_CAP, cut: 1, shadow: false },
  e_far_spire: { clip: false, cap: BASALT_CAP, cut: 0, shadow: false }, e_far_aqueduct: { clip: false, cap: BASALT_CAP, cut: 0, shadow: false },
};
const FLOOR_TEX: Record<StageLookKey, string> = {
  a: 'r1/tex/floor_moss_stone.webp', b: 'village/tex/plaza_cobble.webp', m: 'r1/tex/floor_dirt_arena.webp', f: 'r1/tex/floor_root_moss.webp',
  ea: 'r2/tex/floor_basalt_cobble.webp', eb: 'r2/tex/floor_basalt_cobble.webp', em: 'r2/tex/floor_forge_plate.webp', ef: 'r2/tex/floor_basalt_cobble.webp',
};
/** 지역2 하늘(지평선 색 · 꼭대기 색)과 용암 바다 흐름(m/s) */
const EMBER_SKY: Readonly<Record<string, { low: readonly [number, number, number]; high: readonly [number, number, number] }>> = {
  a: { low: [1.0, 0.3, 0.06], high: [0.06, 0.022, 0.02] }, a2: { low: [1.0, 0.2, 0.12], high: [0.1, 0.02, 0.05] },
  m: { low: [1.0, 0.38, 0.08], high: [0.07, 0.03, 0.016] }, b: { low: [1.1, 0.45, 0.1], high: [0.08, 0.03, 0.016] },
  b2: { low: [0.58, 0.5, 0.48], high: [0.14, 0.13, 0.15] }, f: { low: [1.0, 0.27, 0.08], high: [0.06, 0.022, 0.024] },
};
const LAVA_FLOW: Readonly<Record<string, readonly [number, number]>> = { ea: [0.05, -0.03], eb: [-0.35, 0.12], em: [0.03, 0.04], ef: [0.06, 0.06] };
/** 지역2 랜드마크 세트는 데이터 높이(l.height)로 균일 배율, 큰 장식(도가니·제단·모루)은 정면(남쪽)을 본다 */
const FACING_SETS: ReadonlySet<SetName> = new Set<SetName>(['e_crucible', 'e_altar', 'e_anvil'])

const inRect = (r: Rect, x: number, z: number): boolean => x >= r.x0 && x <= r.x1 && z >= r.z0 && z <= r.z1;

export class StageEnv {
  readonly root = new THREE.Group();
  private readonly owned: { dispose(): void }[] = [];
  private readonly batches: Batch[] = [];
  private started: Promise<EnvResult> | null = null;
  private readonly lastCull = new Float32Array([1e9, 1e9, 0]);

  constructor(private readonly map: MapDef, private readonly grid: MapGrid, private readonly quality: QualityLevel) {
    this.root.name = 'stage-env-' + map.stageKey;
  }

  start(): Promise<EnvResult> {
    this.started ??= this.build();
    return this.started;
  }

  /** 품질 조절기가 끄는 장식 효과(접지 그림자·폭포·풀·꽃·불꽃·빛줄기…): 키트가 없는 지역2 지도에서도 찾도록 표시를 단다(env/decor.ts) */
  private addDecor<T extends THREE.Object3D>(o: T | null): T | null {
    if (o) o.userData[DECOR_FLAG] = true;
    return this.add(o);
  }

  private add<T extends THREE.Object3D>(o: T | null): T | null {
    if (o) {
      this.root.add(o);
      const m = o as unknown as THREE.Mesh;
      if (m.geometry) this.owned.push(m.geometry);
      if (m.material) this.owned.push(m.material as THREE.Material);
    }
    return o;
  }

  private makeBatch(geo: THREE.BufferGeometry, mat: THREE.Material, list: readonly Inst[], shadow: boolean, name: string): void {
    const n = list.length;
    if (n === 0) return;
    const cutSrc = new Float32Array(n * 4);
    const cut = new THREE.InstancedBufferAttribute(new Float32Array(n * 4), 4);
    cut.setUsage(THREE.DynamicDrawUsage);
    // 지오메트리는 맵 사이에서 공유하므로 인스턴스 속성은 사본에 단다(정점 버퍼는 공유)
    const g = new THREE.BufferGeometry();
    if (geo.index) g.setIndex(geo.index);
    for (const a of ['position', 'normal', 'uv']) { const at = geo.getAttribute(a); if (at) g.setAttribute(a, at); }
    g.setAttribute('aCut', cut);
    const mesh = new THREE.InstancedMesh(g, mat, n);
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    const matSrc = new Float32Array(n * 16), colSrc = new Float32Array(n * 3), pos = new Float32Array(n * 3);
    const col = new THREE.Color();
    list.forEach((p, i) => {
      compose(matSrc, i * 16, p.x, p.y, p.z, p.rot, 0, p.sx, p.sy, p.sz);
      cutSrc[i * 4] = p.x; cutSrc[i * 4 + 1] = p.z; cutSrc[i * 4 + 2] = p.cut; cutSrc[i * 4 + 3] = p.sy;
      col.setHex(p.tint);
      colSrc[i * 3] = col.r; colSrc[i * 3 + 1] = col.g; colSrc[i * 3 + 2] = col.b;
      mesh.setColorAt(i, col);
      pos[i * 3] = p.x; pos[i * 3 + 1] = p.z; pos[i * 3 + 2] = p.radius;
    });
    (mesh.instanceMatrix.array as Float32Array).set(matSrc);
    (cut.array as Float32Array).set(cutSrc);
    mesh.instanceMatrix.needsUpdate = true;
    mesh.instanceColor!.setUsage(THREE.DynamicDrawUsage);
    mesh.frustumCulled = false;
    mesh.castShadow = shadow && this.quality !== 'low';
    mesh.receiveShadow = true;
    mesh.name = name;
    this.root.add(mesh);
    this.owned.push(g);
    this.batches.push({ cullR: name.startsWith('set-far_') || name.startsWith('set-e_far_') ? 150 : CULL_RADIUS, mesh, cut, n, mat: matSrc, cutSrc, col: colSrc, pos });
  }

  private async build(): Promise<EnvResult> {
    const map = this.map, grid = this.grid;
    const key = lookOf(map);
    const ember = isEmberLook(key);
    const seaY = map.seaY ?? -14;
    const mood = moodOf(map);
    const haze = new THREE.Color(mood.haze);
    const rnd = mulberry32(9100 + (map.stageKey ?? 'a').charCodeAt(0) * 7 + (map.stageKey?.length ?? 0));
    const density = DENSITY[this.quality];

    // ── 읽기: 바닥 판석 · 원경 · 세트피스 · 랜드마크 키트 ──
    const used = usedSets(map);
    const regions = new Set<'r1' | 'r2'>([...used].map(setRegion));
    const [floorTex, capTex, pano, atlasR1, atlasR2, kitMap] = await Promise.all([
      track(loadTexture(FLOOR_TEX[key]).then((t) => t ?? loadTexture('r1/tex/floor_moss_stone.webp'))),
      track(loadTexture('r1/tex/floor_moss_stone.webp')),
      ember ? Promise.resolve(null) : track(loadTexture('r1/tex/bg_r1_panorama.webp', true)),
      regions.has('r1') ? track(loadSetAtlas('r1')) : Promise.resolve(null),
      regions.has('r2') ? track(loadSetAtlas('r2')) : Promise.resolve(null),
      track(loadKits(map)),
    ]);
    const assets = new Map<SetName, SetAsset>();
    await Promise.all([...used].map(async (n) => { const a = await track(loadSet(n)); if (a) assets.set(n, a); }));

    // ── 원경(가장 먼저, 깊이 없이) ──
    if (ember) {
      // 지역2: 검은 연기 구름 + 지도·시간대별 지평선 색(파노라마 없음)
      const sk = EMBER_SKY[map.stageKey ?? 'a'] ?? EMBER_SKY.a;
      this.add(emberSky(sk.low, sk.high, this.quality !== 'low'));
    } else {
      // 두 파노라마 모두 지평선이 그림 위에서 약 28% 높이(v ≈ 0.72)
      const bd = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), backdropMaterial(pano, haze, 2.5, 0.72, 90));
      bd.frustumCulled = false;
      bd.renderOrder = -1000;
      this.add(bd);
    }

    // ── 바닥 윗면: 다리가 대신하는 자리(bare 구역)는 비운다 ──
    const bridgeKinds: SetName[] = ['bridge', 'bridge_wood', 'bridge_root', 'e_bridge'];
    const bridgeOk = (map.sets ?? []).every((s) => !bridgeKinds.includes(s.kind as SetName) || assets.has(s.kind as SetName));
    const bareZones = bridgeOk ? (map.edgeZones ?? []).filter((z) => z.style === 'bare').map((z) => z.rect) : [];
    const inBare = (x: number, z: number): boolean => bareZones.some((r) => inRect(r, x, z));
    const field = buildField(grid, 4242 + (map.stageKey?.charCodeAt(0) ?? 0));
    const contour = contourOf(field, grid, bareZones.length ? inBare : undefined);

    const proxData = edgeProximity(grid, 3);
    const prox: ProximityTex = { tex: dataTexture(proxData, grid.width, grid.height), originX: grid.originX, originZ: grid.originZ, width: grid.width, height: grid.height };
    this.owned.push(prox.tex);
    {
      const pos: number[] = [];
      for (let i = 0; i < contour.band.length; i += 2) pos.push(contour.band[i], 0, contour.band[i + 1]);
      for (const r of contour.interior) pos.push(r.x0, 0, r.z0, r.x0, 0, r.z1, r.x1, 0, r.z1, r.x0, 0, r.z0, r.x1, 0, r.z1, r.x1, 0, r.z0);
      const g = new THREE.BufferGeometry();
      const arr = new Float32Array(pos);
      // 위를 향하도록 감기지 않은 삼각형은 뒤집는다(사각형 두 장)
      for (let t = 0; t < arr.length; t += 9) {
        const ax = arr[t], az = arr[t + 2], bx = arr[t + 3], bz = arr[t + 5], cx = arr[t + 6], cz = arr[t + 8];
        if ((bz - az) * (cx - ax) - (bx - ax) * (cz - az) < 0) for (let k = 0; k < 3; k++) { const o = t + 3 + k; const p = arr[o]; arr[o] = arr[o + 3]; arr[o + 3] = p; }
      }
      g.setAttribute('position', new THREE.BufferAttribute(arr, 3));
      g.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(arr.length).map((_, i) => (i % 3 === 1 ? 1 : 0)), 3));
      const top = new THREE.Mesh(g, ember ? stageEmberFloorMaterial(floorTex, prox, key as 'ea' | 'eb' | 'em' | 'ef', map.lava ?? []) : stageFloorMaterial(floorTex, prox, key as 'a' | 'b' | 'm' | 'f'));
      top.receiveShadow = true;
      top.name = 'stage-floor';
      this.add(top);
    }
    // 틈막이: 윗면 조각 사이의 미세한 틈이 보이지 않게 바닥 칸 밑에 어두운 판을 깐다
    {
      const cells = cellsOf(map.floors);
      for (const k of [...cells]) { const [cx, cz] = k.split(',').map(Number); if (inBare(cx + 0.5, cz + 0.5)) cells.delete(k); }
      const parts: number[] = [];
      for (const r of rectsOfCells(cells)) parts.push(r.x0, -0.04, r.z0, r.x0, -0.04, r.z1, r.x1, -0.04, r.z1, r.x0, -0.04, r.z0, r.x1, -0.04, r.z1, r.x1, -0.04, r.z0);
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(parts, 3));
      const under = new THREE.Mesh(g, new THREE.MeshBasicMaterial({ color: ember ? new THREE.Color(0.06, 0.05, 0.05) : new THREE.Color(0.2, 0.19, 0.15) }));
      under.name = 'stage-underlay';
      this.add(under);
    }

    // ── 가장자리: 단면 · 트림 · 풀 · 덩굴 ──
    const trims = new Map<SetName, Inst[]>();
    const pushTrim = (n: SetName, i: Inst): void => { let a = trims.get(n); if (!a) trims.set(n, (a = [])); a.push(i); };
    const kitInst = new Map<string, Inst[]>();
    const lamps: Emitter[] = [], glows: Emitter[] = [];
    // 지역2: 가장자리 아래 용암 면에서 떠오르는 불씨 자리(x, y, z)
    const sparks: number[] = [];
    const tufts: Emitter[] = [], flowers: Emitter[] = [], reeds: Emitter[] = [], ivy: (Emitter & { rot: number; w: number })[] = [];
    const cliffPos: number[] = [], cliffIdx: number[] = [];
    const styleAt = (x: number, z: number): string => (inBare(x, z) ? 'bare' : edgeStyleAt(map, x, z));
    const buildCliff = (chain: Chain): void => {
      const dense = resample(chain, 1.0);
      if (dense.length < 2) return;
      const rows = 5;
      let prevBase = -1;
      const emit = (s: (typeof dense)[number], depth: number): number => {
        const base = cliffPos.length / 3;
        const style = styleAt(s.x, s.z);
        const roots = style === 'roots';
        for (let k = 0; k <= rows; k++) {
          const t = k / rows;
          const y = k === 0 ? 0.02 : -depth * Math.pow(t, 1.3);
          let bulge = 0;
          if (depth > 3 && k > 0) {
            const n1 = valueNoise(s.x * 0.7 + k * 3.1, s.z * 0.7 - k * 1.7, 31) - 0.42;
            const n2 = valueNoise(s.x * 2.4, s.z * 2.4 + k * 5.3, 77) - 0.5;
            bulge = (0.22 + n1 * (roots ? 1.9 : 1.15) + n2 * 0.3) * Math.min(1, t * 2.2) * (roots ? 1.25 : 1);
          }
          cliffPos.push(s.x + s.nx * bulge, y, s.z + s.nz * bulge);
        }
        return base;
      };
      for (let i = 0; i < dense.length; i++) {
        const s = dense[i];
        const style = styleAt(s.x, s.z);
        const water = !ember && inWater(map, s.x + s.nx * 1.4, s.z + s.nz * 1.4);
        // 지역2: 절벽은 용암 바다 면 아래 0.3m까지(틈 없이 잠긴다)
        const depth = ember ? -seaY + 0.3 : water ? -WATER_Y + 0.1 : CLIFF_DEPTH;
        if (style === 'bare') { prevBase = -1; continue; }
        const base = emit(s, depth);
        if (prevBase >= 0) {
          for (let k = 0; k < rows; k++) {
            const a = prevBase + k, b = prevBase + k + 1, c = base + k, d = base + k + 1;
            cliffIdx.push(a, c, b, b, c, d);
          }
        }
        prevBase = base;
      }
      // 닫힌 고리는 끝을 첫 열과 잇는다(첫 열 = 0번 표본의 열을 다시 만들지 않고 시작 번호를 쓴다)
      if (chain.closed && prevBase >= 0 && dense.length > 2 && styleAt(dense[0].x, dense[0].z) !== 'bare') {
        const firstBase = prevBase - (dense.length - 1) * (rows + 1);
        if (firstBase >= 0 && cliffPos.length / 3 >= prevBase + rows + 1) {
          for (let k = 0; k < rows; k++) { const a = prevBase + k, b = prevBase + k + 1, c = firstBase + k, d = firstBase + k + 1; cliffIdx.push(a, c, b, b, c, d); }
        }
      }
    };
    const spacing = 2.4;
    const arcadeEvery = arcadeEveryOf(map);
    const arcade = arcadeEvery > 0 ? assets.get('ruin_arcade') : undefined;
    let sampleNo = 0;
    for (const chain of contour.chains) {
      buildCliff(chain);
      const samples = resample(chain, spacing);
      for (const s of samples) {
        sampleNo++;
        // 낭떠러지 단면에 붙은 폐허 아케이드(여덟 표본마다 하나, 아치 아래로 파란 하늘이 보인다)
        if (arcade && sampleNo % arcadeEvery === 3 && styleAt(s.x, s.z) === 'cliff') {
          pushTrim('ruin_arcade', { x: s.x + s.nx * 0.35, y: 0, z: s.z + s.nz * 0.35, rot: Math.atan2(s.nx, s.nz), sx: 1, sy: 1, sz: 1, tint: 0xffffff, cut: 1, radius: 5 });
        }
        const style = styleAt(s.x, s.z);
        if (style === 'bare') continue;
        const yaw = Math.atan2(s.nx, s.nz);
        // 뿌리 테두리는 표본의 40%를 비운다(빽빽한 촉수처럼 보이지 않게) — 그 자리는 안쪽 풀·꽃이 채운다
        if (style === 'roots' && rnd() < 0.55) continue;
        const modules = STYLE_MODULES[style] ?? STYLE_MODULES.cliff;
        const name = modules[Math.floor(rnd() * modules.length)];
        const a = assets.get(name);
        if (a) {
          const lenK = (spacing / Math.max(0.5, a.w)) * (1 + rnd() * 0.12);
          const sc = (0.92 + rnd() * 0.2) * (style === 'roots' ? 0.8 : 1);
          pushTrim(name, { x: s.x - s.nx * 0.12, y: 0, z: s.z - s.nz * 0.12, rot: yaw + (rnd() - 0.5) * 0.12, sx: lenK, sy: sc, sz: sc, tint: tintOf(rnd, key), cut: SET_STYLE[name].cut, radius: spacing });
        }
        // 지역2: 표본 셋마다 가장자리 바깥 아래 용암 면에서 불씨가 올라온다
        if (ember && sampleNo % 3 === 0) sparks.push(s.x + s.nx * (1.2 + rnd() * 2), seaY + 0.2, s.z + s.nz * (1.2 + rnd() * 2));
        // 안쪽 풀·꽃(가장자리 1~2m 안) — 지역2는 풀 대신 재와 소품
        if (!ember && (style !== 'palisade' || rnd() < 0.3)) {
          const tn = Math.round((key === 'm' ? 1.2 : 2.6) * density + rnd());
          for (let k = 0; k < tn; k++) {
            const dd = 0.5 + rnd() * 1.4, u = (rnd() - 0.5) * spacing;
            (rnd() < (key === 'f' ? 0.4 : key === 'a' ? 0.3 : 0.2) ? flowers : tufts).push({ x: s.x - s.nx * dd + s.tx * u, y: 0, z: s.z - s.nz * dd + s.tz * u, s: 0.45 + rnd() * 0.4, v: rnd() });
          }
        }
        // 가장자리 곁 소품(바위·잔해·상자·버섯): 길목(문 곁 4m)·다리 구역은 비운다 — 인스턴싱 저폴리 세트
        if (style !== 'bare' && rnd() < 0.2 * density && !map.gates.some((g) => s.x > g.rect.x0 - 4 && s.x < g.rect.x1 + 4 && s.z > g.rect.z0 - 4 && s.z < g.rect.z1 + 4)) {
          const list = SCATTER_SETS[key];
          const sc = list[Math.floor(rnd() * list.length)], pa = assets.get(sc.kind);
          if (pa) {
            const dd = 1.0 + rnd() * 1.6, px = s.x - s.nx * dd + s.tx * (rnd() - 0.5) * spacing, pz = s.z - s.nz * dd + s.tz * (rnd() - 0.5) * spacing;
            const h = sc.h[0] + rnd() * (sc.h[1] - sc.h[0]), k2 = h / pa.h;
            pushTrim(sc.kind, { x: px, y: 0, z: pz, rot: rnd() * Math.PI * 2, sx: k2, sy: k2, sz: k2, tint: tintOf(rnd, key), cut: 1, radius: 0.5 * Math.max(pa.w * k2, pa.d * k2, h) });
            if (sc.kind === 'prop_mushrooms') glows.push({ x: px, y: h * 0.55, z: pz, s: h * 2.3, v: rnd() });
            if (sc.kind === 'e_prop_slag') lamps.push({ x: px, y: h * 0.4, z: pz, s: h * 2.0, v: rnd() });
          }
        }
        // 수로 광장 가로등: 둑을 따라 열네 표본(약 28m)마다 한 기, 둑 안쪽 0.8m
        if (key === 'b' && style === 'water' && sampleNo % 14 === 5) {
          const lk = kitMap.get('v_lamppost');
          if (lk) {
            const lx = s.x - s.nx * 0.8, lz = s.z - s.nz * 0.8, sc = 3.6 / lk.h;
            let a = kitInst.get('v_lamppost');
            if (!a) kitInst.set('v_lamppost', (a = []));
            a.push({ x: lx, y: 0, z: lz, rot: Math.atan2(s.nx, s.nz), sx: sc, sy: sc, sz: sc, tint: 0xffffff, cut: 1, radius: 1.2 });
            lamps.push({ x: lx, y: 3.5, z: lz, s: 1.1, v: rnd() });
          }
        }
        // 수로 둑 바깥 물속에 선 갈대
        if (key === 'b' && style !== 'bare' && inWater(map, s.x + s.nx * 1.4, s.z + s.nz * 1.4) && rnd() < 0.45 * density) {
          const dd = 0.5 + rnd() * 1.2;
          reeds.push({ x: s.x + s.nx * dd + s.tx * (rnd() - 0.5) * spacing, y: WATER_Y + 0.05, z: s.z + s.nz * dd + s.tz * (rnd() - 0.5) * spacing, s: 0.9 + rnd() * 0.6, v: rnd() });
        }
        // 절벽·뿌리 단면에 늘어진 덩굴
        if (!ember && (style === 'cliff' || style === 'roots' || style === 'parapet') && !inWater(map, s.x + s.nx * 1.4, s.z + s.nz * 1.4) && rnd() < (key === 'm' ? 0.2 : 0.5) * density) {
          ivy.push({ x: s.x + s.nx * 0.2, y: 0.1, z: s.z + s.nz * 0.2, s: 1.4 + rnd() * 3.2, v: rnd(), rot: yaw, w: 1.6 + rnd() * 1.2 });
        }
      }
    }
    if (cliffIdx.length > 0) {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(cliffPos, 3));
      g.setIndex(cliffIdx);
      g.computeVertexNormals();
      const cliff = new THREE.Mesh(g, ember ? stageBasaltCliffMaterial(seaY) : stageCliffMaterial(key as 'a' | 'b' | 'm' | 'f', haze, -CLIFF_DEPTH));
      cliff.name = 'stage-cliff';
      this.add(cliff);
    }

    // ── 용암 바다(지역2): 판 전체 아래 한 장(여백 260m — 원경 첨탑이 그 위에 선다) ──
    if (ember) {
      const shoreData = shoreDistance(grid, 8);
      const shore: ProximityTex = { tex: dataTexture(shoreData, grid.width, grid.height), originX: grid.originX, originZ: grid.originZ, width: grid.width, height: grid.height };
      this.owned.push(shore.tex);
      const M = 260, w = grid.width + M * 2, d = grid.height + M * 2;
      const g = new THREE.PlaneGeometry(w, d, 1, 1);
      g.rotateX(-Math.PI / 2);
      g.translate(grid.originX + grid.width / 2, seaY, grid.originZ + grid.height / 2);
      const sea = this.add(new THREE.Mesh(g, lavaSeaMaterial(shore, LAVA_FLOW[key] ?? [0.05, 0], map.envGlow?.seaFar ?? 1)))!;
      sea.name = 'stage-lava-sea';
      // 위험 칸(용암 웅덩이·분출구 테)과 불씨·연기
      const hz = emberHazards(map);
      hz.traverse((o) => { const m = o as THREE.Mesh; if (m.geometry) this.owned.push(m.geometry); if (m.material) this.owned.push(m.material as THREE.Material); });
      this.root.add(hz);
      this.add(emberAtmosphere(map, false, sparks));
      this.add(emberAtmosphere(map, true));
    }
    // ── 수면(B) ──
    if (!ember && map.water && map.water.length > 0) {
      const shoreData = shoreDistance(grid, 8);
      const shore: ProximityTex = { tex: dataTexture(shoreData, grid.width, grid.height), originX: grid.originX, originZ: grid.originZ, width: grid.width, height: grid.height };
      this.owned.push(shore.tex);
      for (const w of map.water) {
        const g = new THREE.PlaneGeometry(w.x1 - w.x0, w.z1 - w.z0);
        g.rotateX(-Math.PI / 2);
        g.translate((w.x0 + w.x1) / 2, WATER_Y, (w.z0 + w.z1) / 2);
        const water = this.add(new THREE.Mesh(g, stageWaterMaterial(shore, haze)))!;
        water.receiveShadow = true;
        water.name = 'stage-water';
      }
    }

    // ── 지도 앵커 세트피스 + 랜드마크 ──
    const placed = new Map<SetName, Inst[]>();
    const push = (n: SetName, i: Inst): void => { let a = placed.get(n); if (!a) placed.set(n, (a = [])); a.push(i); };
    const flames: Emitter[] = [];
    const waterfalls: SetPieceDef[] = [];
    const lavafalls: SetPieceDef[] = [];
    for (const s of map.sets ?? []) {
      if (s.kind === 'waterfall') { waterfalls.push(s); continue; }
      if (s.kind === 'lavafall') { lavafalls.push(s); continue; }
      const n = s.kind as SetName;
      const a = assets.get(n);
      if (!a) continue;
      const sc = s.scale ?? 1;
      const isBridge = bridgeKinds.includes(n);
      const sz = isBridge && s.len ? s.len / Math.max(1, a.d) : sc;
      push(n, { x: s.x, y: s.y ?? 0, z: s.z, rot: s.rot, sx: isBridge ? 1 : sc, sy: isBridge ? 1 : sc, sz, tint: 0xffffff, cut: SET_STYLE[n].cut, radius: Math.max(a.w, a.d, a.h) * 0.5 * Math.max(sc, sz) });
      if (n === 'bonfire') flames.push({ x: s.x, y: 1.15 * sc, z: s.z, s: 1.1 * sc, v: rnd() });
      // 지역2 문틀: 두 기둥 위 화로 바구니(정면 +z 기준 x ±2.9)
      if (n === 'e_gate') for (const side of [-1, 1]) flames.push({ x: s.x + Math.cos(s.rot) * side * 2.9 * sc, y: 5.05 * sc, z: s.z - Math.sin(s.rot) * side * 2.9 * sc, s: 0.9 * sc, v: rnd() });
    }
    const replaced = new Set<number>();
    const tops = new Map<number, number>();
    map.landmarks.forEach((l, i) => {
      const w = l.rect.x1 - l.rect.x0, d = l.rect.z1 - l.rect.z0;
      const cx = (l.rect.x0 + l.rect.x1) / 2, cz = (l.rect.z0 + l.rect.z1) / 2;
      const rot = rnd() * Math.PI * 2;
      const fit = (a: { w: number; h: number; d: number }, tw: number, th: number, td: number): [number, number, number] => [tw / a.w, th / a.h, td / a.d];
      const kitPush = (kind: KitName, th: number, tw: number, td: number): boolean => {
        const k = kitMap.get(kind);
        if (!k) return false;
        const [sx, sy, sz] = th > 0 && tw === 0 ? [th / k.h, th / k.h, th / k.h] : fit(k, tw, th, td);
        let a = kitInst.get(kind);
        if (!a) kitInst.set(kind, (a = []));
        a.push({ x: cx, y: 0, z: cz, rot, sx, sy, sz, tint: 0xffffff, cut: 2, radius: 0.5 * Math.max(k.w * sx, k.d * sz, k.h * sy) });
        return true;
      };
      let ok = false;
      switch (l.kind) {
        case 'pillar': {
          const s = l.height / 4.5;
          ok = l.height < 5 && rnd() < 0.25 ? kitPush('pillar_broken', 2.4, 1.9, 1.9) : kitPush('pillar', l.height, 1.55 * s, 1.55 * s);
          break;
        }
        case 'rubble': ok = kitPush('rubble', Math.max(1.25, l.height), w * 0.95, d * 0.9); break;
        case 'statue': ok = kitPush('statue', 4.8, 0, 0); break;
        case 'tower': case 'bonfire': case 'throne': case 'root': {
          if (map.id === 'r2') break;
          const n: SetName = l.kind === 'root' ? 'root_knot' : l.kind;
          const a = assets.get(n);
          if (a) {
            const sx = w / a.w, sz = d / a.d, sy = n === 'throne' ? Math.min(sx, sz) : (l.kind === 'root' ? l.height / a.h : Math.min(sx, sz));
            push(n, { x: cx, y: 0, z: cz, rot: l.kind === 'throne' ? Math.PI * 0 : rot, sx, sy, sz, tint: 0xffffff, cut: SET_STYLE[n].cut, radius: Math.max(a.w * sx, a.d * sz, a.h * sy) * 0.55 });
            if (l.kind === 'bonfire') flames.push({ x: cx, y: 1.15 * sy, z: cz, s: 1.25, v: rnd() });
            if (l.kind === 'tower') flames.push({ x: cx, y: 6.55 * sy, z: cz, s: 1.0, v: rnd() });
            tops.set(i, a.h * sy);
            ok = true;
          }
          break;
        }
        default: break;
      }
      // 지역2: 모든 랜드마크가 세트피스(현무암 기둥 무리·화로 탑·세발 화로·도가니·모루·제단·오벨리스크). 데이터 높이로 균일 배율
      if (map.id === 'r2') {
        const n = landmarkSet(map, l.kind), a = n ? assets.get(n) : undefined;
        if (n && a) {
          const sc = l.height / a.h;
          const r = FACING_SETS.has(n) ? 0 : rot;
          push(n, { x: cx, y: 0, z: cz, rot: r, sx: sc, sy: sc, sz: sc, tint: 0xffffff, cut: SET_STYLE[n].cut, radius: Math.max(a.w, a.d, a.h) * 0.55 * sc });
          if (n === 'e_brazier') flames.push({ x: cx, y: l.height - 2.0 * sc, z: cz, s: 1.3 * sc, v: rnd() });
          if (n === 'e_fire_bowl') flames.push({ x: cx, y: 1.05 * sc, z: cz, s: 1.0, v: rnd() });
          if (n === 'e_altar') flames.push({ x: cx, y: 2.2 * sc, z: cz + 0.6 * sc, s: 2.2 * sc, v: rnd() });
          if (n === 'e_crucible') lamps.push({ x: cx, y: 4.2 * sc, z: cz + 0.6 * sc, s: 4.5 * sc, v: rnd() });
          tops.set(i, a.h * sc);
          ok = true;
        }
      }
      if (ok) replaced.add(i);
    });

    // ── 인스턴싱 ──
    const stoneForCap = capTex;
    const matCache = new Map<string, THREE.MeshStandardNodeMaterial>();
    const matFor = (n: SetName): THREE.Material => {
      const st = SET_STYLE[n];
      const k = (st.clip ? 'c' : 'n') + st.cap.join(',');
      const reg = setRegion(n), k2 = reg + k;
      let m = matCache.get(k2);
      if (!m) { m = setMaterial((reg === 'r2' ? atlasR2 : atlasR1) ?? { map: null, emissive: null } as SetAtlas, { clip: st.clip, cap: st.cap, emissive: true, glow: (reg === 'r2' ? 2.6 : 1.5) * (map.envGlow?.sets ?? 1) }); matCache.set(k2, m); this.owned.push(m); }
      return m;
    };
    // QA 진단: ?nosets 이면 세트피스·키트 배치를 끄고 지형만 본다(회색 얼룩이 지형인지 조각인지 가르는 용도)
    const noSets = typeof location !== 'undefined' && /[?&]nosets(&|$)/.test(location.search);
    for (const [n, list] of noSets ? [] : [...trims, ...placed]) {
      const a = assets.get(n);
      if (!a) continue;
      this.makeBatch(a.geometry, matFor(n), list, SET_STYLE[n].shadow, 'set-' + n);
    }
    // 같은 이름이 트림과 앵커 양쪽에 있으면 위에서 두 배치로 나뉜다(드로우콜 +1 이내)
    for (const [kind, list] of noSets ? [] : kitInst) {
      const k = kitMap.get(kind as KitName);
      if (!k) continue;
      const mat = kitMaterial(k, kind as KitName, stoneForCap, false, null);
      this.owned.push(mat);
      this.makeBatch(k.geometry, mat, list, true, 'kit-' + kind);
    }

    // ── 접지 그림자: 큰 소품이 바닥에 떠 보이지 않게 발밑을 어둡게(드로우콜 +1) ──
    {
      const contacts: { x: number; y: number; z: number; rx: number; rz: number; rot: number; a: number }[] = [];
      const GROUNDED: ReadonlySet<SetName> = new Set<SetName>(['tower', 'throne', 'bonfire', 'root_knot', 'banner_pole', 'bone_pile', 'ruin_arch', 'palisade_gate', 'root_gate',
        'e_brazier', 'e_basalt', 'e_obelisk', 'e_crucible', 'e_altar', 'e_anvil', 'e_banner', 'e_gate', 'e_fire_bowl']);
      for (const [n, list] of placed) if (GROUNDED.has(n) && assets.has(n)) for (const i of list) {
        const gate = n === 'palisade_gate' || n === 'root_gate' || n === 'ruin_arch' || n === 'e_gate';
        const rr = gate ? 0.9 : Math.min(i.radius * 0.95, 5.2) + 0.3;
        contacts.push({ x: i.x, y: i.y, z: i.z, rx: n === 'throne' ? rr * 1.5 : rr, rz: gate ? 3.2 : rr, rot: i.rot + (gate ? Math.PI / 2 : 0), a: n === 'bonfire' || n === 'e_fire_bowl' ? 0.35 : 0.5 });
      }
      for (const list of kitInst.values()) for (const i of list) contacts.push({ x: i.x, y: 0, z: i.z, rx: Math.min(i.radius, 2.2) * 0.7 + 0.5, rz: Math.min(i.radius, 2.2) * 0.7 + 0.5, rot: i.rot, a: 0.5 });
      this.addDecor(contactField(contacts));
    }

    // ── 물줄기(코드로 만든 폭포 판) · 용암 폭포 ──
    if (waterfalls.length > 0) this.addDecor(buildWaterfalls(waterfalls, waterfallMaterial(haze), 'stage-waterfalls'));
    if (lavafalls.length > 0) this.addDecor(buildWaterfalls(lavafalls, lavafallMaterial(), 'stage-lavafalls'));

    // ── 효과: 풀·꽃·덩굴·불꽃·빛줄기 ──
    this.addDecor(cardField(tufts, 'tuft', false));
    this.addDecor(cardField(flowers, 'flower', false));
    this.addDecor(cardField(reeds, 'reed', false));
    if (lamps.length > 0) this.addDecor(glowField(lamps, [0.9, 0.42, 0.12]));
    if (glows.length > 0) this.addDecor(glowField(glows));
    this.addDecor(ivyField(ivy));
    if (flames.length > 0) this.addDecor(flameField(flames));
    const shafts: Emitter[] = [];
    for (const room of map.rooms) {
      const a = room.area;
      for (let k = 0; k < 2; k++) shafts.push({ x: a.x0 + (a.x1 - a.x0) * (0.3 + 0.4 * rnd()), y: 0, z: a.z0 + (a.z1 - a.z0) * (0.3 + 0.4 * rnd()), s: 4.4 + rnd() * 2.4, v: rnd() });
    }
    const sunDir = new THREE.Vector3(mood.sunX, mood.sunY, mood.sunZ);
    // 빛줄기: 지역1 숲 햇살만(지역2 연기 하늘에는 해 기둥이 서지 않는다)
    if (!ember) this.addDecor(shaftField(shafts, sunDir, key === 'f' ? [1.0, 0.86, 0.5] : [1.0, 0.84, 0.55], key === 'f' ? 0.62 : 0.5));
    const arena = map.rooms[map.rooms.length - 1];
    // 룬 고리(F 아레나): 지역1 녹색 발광(보스 앞), 지역2 제단은 주황 고리(아레나 가운데)
    if (key === 'f') this.add(runeRingMesh(0, arena.bossAt!.z + 12, 15.5, 10.4));
    if (key === 'ef') { const ar = arena.area; this.add(runeRingMesh((ar.x0 + ar.x1) / 2, (ar.z0 + ar.z1) / 2, 15.5, 10.4, [1.0, 0.34, 0.05])); }

    // 아레나 바닥 상감(A·B 계열 · 지역2 A·M): 넓은 맨 판석 한가운데 겹 고리·방위 살
    if (key === 'a' || key === 'b' || key === 'ea' || key === 'em') {
      const ar = arena.area;
      const rr = Math.min(ar.x1 - ar.x0, ar.z1 - ar.z0) * 0.26;
      this.add(inlayMedallionMesh((ar.x0 + ar.x1) / 2, (ar.z0 + ar.z1) / 2, rr, key === 'a' ? 'ruin' : key === 'b' ? 'quay' : 'ember'));
    }

    return { replaced, tops, walls: true, floor: true };
  }

  /** 매 프레임 장식 배우 애니메이션(스테이지 지도는 없다) */
  animate(_nowMs: number): void { void _nowMs; }

  /** 카메라 시야 사각형 + 여유 안의 인스턴스만 앞쪽으로 모아 그린다. camera가 null이면 모두(미리 컴파일용) */
  cull(camera: THREE.PerspectiveCamera | null): void {
    const b = this.batches;
    if (b.length === 0) return;
    if (!camera) { for (const k of b) this.fill(k, -1e9, 1e9, -1e9, 1e9, 0, 0, 1e9); this.lastCull[0] = 1e9; return; }
    const cp = camera.position, lc = this.lastCull;
    if (Math.abs(cp.x - lc[0]) < 1.5 && Math.abs(cp.z - lc[1]) < 1.5 && Math.abs(cp.y - lc[2]) < 1.5) return;
    lc[0] = cp.x; lc[1] = cp.z; lc[2] = cp.y;
    let x0 = 1e9, x1 = -1e9, z0 = 1e9, z1 = -1e9;
    camera.updateMatrixWorld();
    for (let c = 0; c < 4; c++) {
      _ndc.set(c & 1 ? 1 : -1, c & 2 ? 1 : -1, 0.5).unproject(camera);
      _dir.copy(_ndc).sub(cp).normalize();
      for (let hgt = 0; hgt <= 7; hgt += 7) {
        const t = _dir.y < -1e-3 ? Math.min(120, (cp.y - hgt) / -_dir.y) : 120;
        const x = cp.x + _dir.x * t, z = cp.z + _dir.z * t;
        if (x < x0) x0 = x; if (x > x1) x1 = x; if (z < z0) z0 = z; if (z > z1) z1 = z;
      }
    }
    // 화면 가운데 시선이 바닥에 닿는 점
    _ndc.set(0, 0, 0.5).unproject(camera);
    _dir.copy(_ndc).sub(cp).normalize();
    const tc = _dir.y < -1e-3 ? Math.min(70, cp.y / -_dir.y) : 30;
    const cx = cp.x + _dir.x * tc, cz = cp.z + _dir.z * tc;
    for (const k of b) this.fill(k, x0 - CULL_MARGIN, x1 + CULL_MARGIN, z0 - CULL_MARGIN, z1 + CULL_MARGIN, cx, cz, k.cullR);
  }

  private fill(k: Batch, x0: number, x1: number, z0: number, z1: number, cx: number, cz: number, radius: number): void {
    const dm = k.mesh.instanceMatrix.array as Float32Array, dc = k.cut.array as Float32Array, dcol = k.mesh.instanceColor!.array as Float32Array;
    const { pos, mat, cutSrc, col } = k;
    let m = 0;
    for (let i = 0; i < k.n; i++) {
      const x = pos[i * 3], z = pos[i * 3 + 1], r = pos[i * 3 + 2];
      if (x + r < x0 || x - r > x1 || z + r < z0 || z - r > z1) continue;
      if (Math.hypot(x - cx, z - cz) - r > radius) continue;
      for (let j = 0; j < 16; j++) dm[m * 16 + j] = mat[i * 16 + j];
      for (let j = 0; j < 4; j++) dc[m * 4 + j] = cutSrc[i * 4 + j];
      for (let j = 0; j < 3; j++) dcol[m * 3 + j] = col[i * 3 + j];
      m++;
    }
    k.mesh.count = m;
    k.mesh.instanceMatrix.needsUpdate = true;
    k.cut.needsUpdate = true;
    k.mesh.instanceColor!.needsUpdate = true;
  }

  dispose(): void {
    for (const o of this.owned) o.dispose();
    this.owned.length = 0;
    this.root.clear();
  }
}

type KitName = 'pillar' | 'pillar_broken' | 'rubble' | 'statue' | 'v_lamppost';
/** 랜드마크가 쓰는 기본 키트(이끼 유적 r1): 기둥·부러진 기둥·잔해·여신상 */
async function loadKits(map: MapDef): Promise<Map<KitName, KitAsset>> {
  const kinds = new Set<KitName>();
  for (const l of map.landmarks) {
    if (l.kind === 'pillar') { kinds.add('pillar'); kinds.add('pillar_broken'); }
    if (l.kind === 'rubble') kinds.add('rubble');
    if (l.kind === 'statue') kinds.add('statue');
  }
  if (map.id === 'r1' && (map.stageKey === 'b' || map.stageKey === 'b2')) kinds.add('v_lamppost');
  // 지역2는 랜드마크를 모두 세트피스로 그린다(이끼 유적 키트를 읽지 않는다)
  if (map.id === 'r2') kinds.clear();
  const out = new Map<KitName, KitAsset>();
  await Promise.all([...kinds].map(async (k) => { const a = await loadKit(k, null); if (a) out.set(k, a); }));
  return out;
}

/** 인스턴스 색 변주: 이끼빛·회색·밝은 돌을 섞어 같은 조각 반복을 숨긴다 */
function tintOf(rnd: () => number, key: StageLookKey): number {
  const t = rnd();
  if (isEmberLook(key)) return t < 0.3 ? 0xe6dcd6 : t < 0.6 ? 0xffffff : 0xf4e6dc;
  if (key === 'm' || key === 'f') return t < 0.3 ? 0xe8e0d0 : t < 0.6 ? 0xffffff : 0xf4ecdc;
  return t < 0.3 ? 0xcfe3bd : t < 0.5 ? 0xdcd5c9 : t < 0.6 ? 0xfff6e6 : 0xffffff;
}

/** 폭포: 휘어진 흰 물줄기 띠 + 바닥의 물안개. 위가 좁고 아래가 넓다. 인스턴스가 아니라 소수의 판 한 메시로 합친다(코드 생성). 용암 폭포도 같은 띠(재질만 다르다) */
function buildWaterfalls(list: readonly SetPieceDef[], m: THREE.Material, name: string): THREE.Mesh {
  const pos: number[] = [], uvs: number[] = [], idx: number[] = [];
  for (const s of list) {
    // y = 폭포 윗끝 높이(낭떠러지 가장자리 근처), 아래로 h만큼 떨어진다(len이 있으면 그 낙차 — 용암 바다까지)
    const sc = s.scale ?? 1, w = 3.6 * sc, h = s.len ?? 34 * sc, y0 = (s.y ?? 0);
    const cos = Math.cos(s.rot), sin = Math.sin(s.rot);
    const base = pos.length / 3;
    const nx = 6, ny = 10;
    for (let j = 0; j <= ny; j++) {
      for (let i = 0; i <= nx; i++) {
        const u = i / nx - 0.5, v = j / ny;
        const ww = w * (0.7 + 0.5 * v) * u;
        // 정면 +z 기준으로 만든 뒤 rot로 돌려 놓는다. 위쪽은 낭떠러지 쪽으로 살짝 휜다
        const lx = ww, lz = 0.6 * (1 - v) * (1 - v) * 2;
        pos.push(s.x + lx * cos + lz * sin, y0 - h * v, s.z - lx * sin + lz * cos);
        uvs.push(i / nx, v);
      }
    }
    for (let j = 0; j < ny; j++) for (let i = 0; i < nx; i++) {
      const a = base + j * (nx + 1) + i, b = a + 1, c = a + nx + 1, d = c + 1;
      idx.push(a, c, b, b, c, d);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  g.setIndex(idx);
  const mesh = new THREE.Mesh(g, m);
  mesh.frustumCulled = false;
  mesh.name = name;
  return mesh;
}

function waterfallMaterial(haze: THREE.Color): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide });
  const p = uv();
  // 세로로 흐르는 줄무늬(느린 속도), 가장자리는 투명, 아래로 갈수록 물안개에 섞인다
  const streak = mx_noise_float(vec3(p.x.mul(14), p.y.mul(3.2).add(time.mul(0.9)), 1.3)).mul(0.5).add(0.5);
  const streak2 = mx_noise_float(vec3(p.x.mul(31), p.y.mul(7).add(time.mul(1.6)), 5.1)).mul(0.5).add(0.5);
  const edge = float(1).sub(smoothstep(0.3, 0.48, abs(p.x.sub(0.5))));
  // 흰 줄기와 틈이 또렷하게 갈린다(뿌연 구름 대신 흘러내리는 물줄기), 윗끝은 밝은 턱, 아래는 물보라
  const body = smoothstep(0.42, 0.66, streak.mul(0.62).add(streak2.mul(0.5)));
  const lip = float(1).sub(smoothstep(0.0, 0.05, p.y));
  const spray = smoothstep(0.86, 1.0, p.y).mul(streak2.mul(0.6).add(0.4));
  const a = edge.mul(smoothstep(0.0, 0.03, p.y)).mul(body.mul(0.85).add(lip.mul(0.9)).add(spray.mul(0.5))).clamp(0, 0.95);
  const white = mix(vec3(0.78, 0.9, 0.98), vec3(1.0, 1.0, 1.0), streak.mul(0.8).add(lip));
  m.colorNode = mix(white, vec3(haze.r, haze.g, haze.b).mul(1.1), smoothstep(0.6, 1.0, p.y).mul(0.35));
  m.opacityNode = a;
  return m;
}
